import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { fetchAllianceBoards } from '../alli/api/allianceBoardApi';
import { fetchAllianceMilestoneProjectsAll } from '../alli/api/allianceMilestoneApi';
import {
  fetchAllianceSpaceRolePermission,
  saveAllianceSpaceRolePermission,
} from '../alli/api/allianceSpaceRolePermissionApi';
import {
  BOARD_TREE_GROUPS,
  boardPermKey,
  buildInitialPermissions,
  collectBoardLeafKeys,
  defaultBoardPerm,
  defaultBoardPermForMemberRoleCd,
  isAllianceBoardLeaf,
  mergeBoardSlots,
  mergeMilestoneSlots,
  milestoneProjectPermKey,
} from '../alli/members/consortiumRolePermissionModel';

/** 권한 관리 섹션 제목(`h6 fw-semibold text-dark mb-2`)과 동일한 라벨 타이포 */
const FIELD_LABEL = 'form-label h6 fw-semibold text-dark mb-2';

const CRUD_KEYS = [
  { key: 'view', label: '조회', descSuffix: '목록·상세를 볼 수 있습니다.' },
  { key: 'create', label: '등록', descSuffix: '새 항목을 만들 수 있습니다.' },
  { key: 'update', label: '수정', descSuffix: '기존 항목을 수정할 수 있습니다.' },
  { key: 'delete', label: '삭제', descSuffix: '항목을 삭제할 수 있습니다.' },
];

const MANAGE_KEY = {
  key: 'manage',
  label: '관리',
  desc: '운영 권한입니다. 게시판·영역 전체 글 숨김/표시·삭제 등이 포함됩니다. 선택하지 않으면 위 CRUD 권한만 적용됩니다.',
};

/** initialData.milestoneTree: 프로젝트 카드 배열 또는 레거시 { project, items }[] */
function normalizeMilestoneProjects(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  raw.forEach((entry) => {
    if (entry == null) return;
    if (entry.project != null && typeof entry.project === 'object') {
      const p = entry.project;
      if (p.id != null && p.id !== '') out.push(p);
      return;
    }
    if (entry.id != null && entry.id !== '') out.push(entry);
  });
  return out;
}

const CONSORTIUM_MEMBER_ITEMS = [
  {
    key: 'memberView',
    title: '회원 조회',
    description: '컨소시엄(스페이스) 멤버 목록·상세 정보를 조회할 수 있습니다.',
  },
  {
    key: 'invite',
    title: '멤버 초대',
    description: '새 멤버를 스페이스에 초대할 수 있습니다.',
  },
  {
    key: 'roleChange',
    title: '역할 변경',
    description: '멤버의 역할(협력체 책임자·주관 연구책임자 등)을 변경할 수 있습니다.',
  },
  {
    key: 'accessSuspend',
    title: '접근 중지',
    description: '참여는 유지하되 스페이스 콘텐츠 접근을 일시 중지할 수 있습니다.',
  },
  {
    key: 'memberRemove',
    title: '퇴장·탈퇴 처리',
    description: '멤버를 스페이스에서 퇴장·정리(탈퇴 처리)할 수 있습니다.',
  },
];

function PermissionCheck({
  id,
  checked,
  onChange,
  title,
  description,
  marginClass = 'mb-3',
}) {
  return (
    <div className={`form-check ${marginClass}`}>
      <input
        type="checkbox"
        id={id}
        className="form-check-input"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label className="form-check-label small" htmlFor={id}>
        <span className="fw-semibold d-block text-dark">{title}</span>
        <span className="text-muted">{description}</span>
      </label>
    </div>
  );
}

function CrudAndManageGroup({
  sectionId,
  title,
  lead,
  perm,
  onToggle,
  withManage,
  subjectLabel,
  isLast,
}) {
  const blockSpacing = isLast ? 'mb-0 pb-0' : 'mb-4 pb-3';
  const borderClass = isLast ? '' : 'border-bottom border-light-subtle';
  return (
    <div className={`${blockSpacing} ${borderClass}`.trim()}>
      <h4 className="h6 fw-semibold text-dark mb-1">{title}</h4>
      {lead ? <p className="small text-muted mb-2">{lead}</p> : null}
      {CRUD_KEYS.map(({ key, label, descSuffix }, idx) => {
        const lastCrudOnly = isLast && !withManage && idx === CRUD_KEYS.length - 1;
        return (
          <PermissionCheck
            key={key}
            id={`${sectionId}-${key}`}
            checked={perm[key]}
            onChange={() => onToggle(sectionId, key)}
            title={`${label} — ${subjectLabel}`}
            description={`${subjectLabel}을(를) ${descSuffix}`}
            marginClass={lastCrudOnly ? 'mb-0' : 'mb-3'}
          />
        );
      })}
      {withManage ? (
        <PermissionCheck
          id={`${sectionId}-manage`}
          checked={perm.manage}
          onChange={() => onToggle(sectionId, 'manage')}
          title={MANAGE_KEY.label}
          description={MANAGE_KEY.desc}
          marginClass={isLast ? 'mb-0' : 'mb-3'}
        />
      ) : null}
    </div>
  );
}

function LeafPermissionRow({ boards, permKey, label, onToggleBoard, depth = 0, emptyBoardFallback }) {
  const p = boards[permKey] || { ...(emptyBoardFallback ?? defaultBoardPerm()) };
  const pathPrefix = `perm-${String(permKey).replace(/[^a-zA-Z0-9_-]/g, '_')}`;

  return (
    <div
      className="rounded-2 bg-white bg-opacity-80 border border-light px-2 py-2 mb-3"
      style={{ marginLeft: Math.min(depth * 14, 56) }}
    >
      <p className="small fw-semibold text-dark mb-2 mb-md-2">{label}</p>
      {CRUD_KEYS.map(({ key, label: crudLabel, descSuffix }, idx) => (
        <PermissionCheck
          key={key}
          id={`${pathPrefix}-${key}`}
          checked={p[key]}
          onChange={() => onToggleBoard(permKey, key)}
          title={crudLabel}
          description={`「${label}」 ${descSuffix}`}
          marginClass={idx === CRUD_KEYS.length - 1 ? 'mb-2' : 'mb-2'}
        />
      ))}
      <PermissionCheck
        id={`${pathPrefix}-manage`}
        checked={p.manage}
        onChange={() => onToggleBoard(permKey, 'manage')}
        title={MANAGE_KEY.label}
        description={MANAGE_KEY.desc}
        marginClass="mb-0"
      />
    </div>
  );
}

function BoardLeafPermissionRow({ groupCd, node, boards, onToggleBoard, depth, emptyBoardFallback }) {
  const uid = node.boardUid ?? node.id;
  const k = boardPermKey(groupCd, uid);
  const name = node.label || node.boardNm || '게시판';
  return (
    <LeafPermissionRow
      boards={boards}
      permKey={k}
      label={name}
      onToggleBoard={onToggleBoard}
      depth={depth}
      emptyBoardFallback={emptyBoardFallback}
    />
  );
}

function MilestoneFolderPermissionBlock({ milestoneProjects, boards, onToggleBoard, isLast, emptyBoardFallback }) {
  const blockSpacing = isLast ? 'mb-0 pb-0' : 'mb-4 pb-3';
  const borderClass = isLast ? '' : 'border-bottom border-light-subtle';
  const list = milestoneProjects || [];
  const hasAny = list.some((p) => p?.id != null && p.id !== '');

  return (
    <div className={`${blockSpacing} ${borderClass}`.trim()}>
      <h4 className="h6 fw-semibold text-dark mb-1">마일스톤</h4>
      <p className="small text-muted mb-2">
        마일스톤 프로젝트(폴더) 단위로 조회·등록·수정·삭제·관리 권한을 둡니다. 해당 폴더 안의 게시물(항목)은 이 권한을 따릅니다.
      </p>
      {!hasAny ? (
        <p className="small text-muted mb-0 fst-italic">등록된 마일스톤 프로젝트가 없습니다.</p>
      ) : (
        list.map((project) => {
          const pUid = project?.id;
          if (pUid == null || pUid === '') return null;
          const pTitle = project?.title || `프로젝트 ${pUid}`;
          const pk = milestoneProjectPermKey(pUid);
          return (
            <LeafPermissionRow
              key={pk}
              boards={boards}
              permKey={pk}
              label={`프로젝트(폴더) — ${pTitle}`}
              onToggleBoard={onToggleBoard}
              depth={0}
              emptyBoardFallback={emptyBoardFallback}
            />
          );
        })
      )}
    </div>
  );
}

function AllianceBoardTreePermissionBlock({
  title,
  lead,
  groupCd,
  nodes,
  boards,
  onToggleBoard,
  isLast,
  emptyBoardFallback,
}) {
  const blockSpacing = isLast ? 'mb-0 pb-0' : 'mb-4 pb-3';
  const borderClass = isLast ? '' : 'border-bottom border-light-subtle';

  const renderNodes = (list, depth) =>
    (list || []).map((node) => {
      if (isAllianceBoardLeaf(node)) {
        return (
          <BoardLeafPermissionRow
            key={boardPermKey(groupCd, node.boardUid ?? node.id)}
            groupCd={groupCd}
            node={node}
            boards={boards}
            onToggleBoard={onToggleBoard}
            depth={depth}
            emptyBoardFallback={emptyBoardFallback}
          />
        );
      }
      return (
        <div key={node.id} className="mb-2" style={{ marginLeft: Math.min(depth * 10, 40) }}>
          <div className="small fw-medium text-secondary mb-2 d-flex align-items-center gap-1">
            <span className="text-muted" aria-hidden>
              └
            </span>
            {node.label || node.boardNm || '폴더'}
          </div>
          {node.children?.length ? renderNodes(node.children, depth + 1) : null}
        </div>
      );
    });

  const hasLeaves = collectBoardLeafKeys(groupCd, nodes).length > 0;

  return (
    <div className={`${blockSpacing} ${borderClass}`.trim()}>
      <h4 className="h6 fw-semibold text-dark mb-1">{title}</h4>
      <p className="small text-muted mb-2">{lead}</p>
      {!hasLeaves ? (
        <p className="small text-muted mb-0 fst-italic">
          이 그룹에 표시할 게시판이 없습니다. (백엔드에 해당 그룹 트리가 없거나 아직 메뉴가 없을 수 있습니다.)
        </p>
      ) : (
        renderNodes(nodes, 0)
      )}
    </div>
  );
}

/**
 * Alliance Space 역할 관리 (역할명·설명·권한 체크)
 * config.initialData:
 *   - roleRow, spaceId (필수: 게시판·마일스톤 원격 조회)
 *   - boardTrees?: { TASK_INFO?, COOPERATION? } — 전달 시 해당 트리는 API 생략
 *   - milestoneTree?: 프로젝트 카드 배열 또는 레거시 { project, items }[] — 전달 시 마일스톤 API 생략(프로젝트만 사용)
 *   - dynamicMenus?, initialPermissions? (boards: GROUP_CD:boardUid 및 MILESTONE_P:projectUid)
 *
 * onClose(submit, { roleName, description, permissions, roleRow })
 */
const ConsortiumRoleManagePopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const {
    roleRow,
    dynamicMenus = [],
    spaceId: spaceIdProp,
    boardTrees: boardTreesProp,
    milestoneTree: milestoneTreeProp,
  } = config?.initialData || {};

  const [roleName, setRoleName] = useState(() => roleRow?.roleLabelKo || '');
  const [description, setDescription] = useState(() => roleRow?.description || '');
  const [perms, setPerms] = useState(() =>
    buildInitialPermissions(
      roleRow,
      config?.initialData?.initialPermissions ?? roleRow?.permissions,
      dynamicMenus,
      undefined,
    ),
  );
  /** DB에서 불러온 권한 — spaceId+역할이 있으면 fetch 후 객체, 로딩 중 undefined */
  const [serverPermissions, setServerPermissions] = useState(undefined);

  const [boardTrees, setBoardTrees] = useState(() => ({
    TASK_INFO: Array.isArray(boardTreesProp?.TASK_INFO) ? boardTreesProp.TASK_INFO : [],
    COOPERATION: Array.isArray(boardTreesProp?.COOPERATION) ? boardTreesProp.COOPERATION : [],
  }));
  const [milestoneProjects, setMilestoneProjects] = useState(() => normalizeMilestoneProjects(milestoneTreeProp));
  const [treesLoading, setTreesLoading] = useState(false);
  const [boardTreesError, setBoardTreesError] = useState(null);
  const [milestoneTreeError, setMilestoneTreeError] = useState(null);

  const roleKey = roleRow?.id ?? roleRow?.roleKey;
  const boardSlotFallback = useMemo(() => defaultBoardPermForMemberRoleCd(roleKey), [roleKey]);
  const dynamicMenuSig = useMemo(
    () => JSON.stringify((dynamicMenus || []).map((m) => [m?.id ?? m?.menuId, m?.name ?? m?.menuName ?? m?.title])),
    [dynamicMenus],
  );

  const spaceId = spaceIdProp != null && String(spaceIdProp).trim() !== '' ? String(spaceIdProp).trim() : null;
  /** 부모가 boardTrees / milestoneTree 를 넘기면 해당 API 재조회 생략 */
  const skipBoardFetch = boardTreesProp != null && typeof boardTreesProp === 'object';
  const skipMilestoneFetch = milestoneTreeProp !== undefined;

  useEffect(() => {
    const data = config?.initialData;
    const row = data?.roleRow;
    if (!row) return;
    setRoleName(row.roleLabelKo || '');
    setDescription(row.description || '');
    if (data?.boardTrees && typeof data.boardTrees === 'object') {
      setBoardTrees({
        TASK_INFO: data.boardTrees.TASK_INFO ?? [],
        COOPERATION: data.boardTrees.COOPERATION ?? [],
      });
    }
    if (data?.milestoneTree !== undefined) {
      setMilestoneProjects(normalizeMilestoneProjects(data.milestoneTree));
    }
  }, [roleKey, dynamicMenuSig, config?.initialData]);

  useEffect(() => {
    if (!spaceId || !roleRow?.id) {
      setServerPermissions(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const entry = await fetchAllianceSpaceRolePermission(spaceId, roleRow.id);
        if (cancelled) return;
        const p = entry?.permissions;
        setServerPermissions(p && typeof p === 'object' ? p : {});
      } catch (e) {
        console.error(e);
        if (!cancelled) setServerPermissions({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceId, roleRow?.id]);

  useEffect(() => {
    const meta = serverPermissions?.meta;
    if (!meta || typeof meta !== 'object') return;
    const nextRoleName = String(meta.roleName || '').trim();
    const nextDesc = String(meta.description || '').trim();
    if (nextRoleName) setRoleName(nextRoleName);
    if (nextDesc) setDescription(nextDesc);
  }, [serverPermissions]);

  useEffect(() => {
    if (!spaceId || (skipBoardFetch && skipMilestoneFetch)) return;
    let cancelled = false;
    setTreesLoading(true);
    setBoardTreesError(null);
    setMilestoneTreeError(null);
    (async () => {
      try {
        if (!skipBoardFetch) {
          const [taskInfo, cooperation] = await Promise.all([
            fetchAllianceBoards(spaceId, 'TASK_INFO'),
            fetchAllianceBoards(spaceId, 'COOPERATION'),
          ]);
          if (!cancelled) {
            setBoardTrees({
              TASK_INFO: Array.isArray(taskInfo) ? taskInfo : [],
              COOPERATION: Array.isArray(cooperation) ? cooperation : [],
            });
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setBoardTreesError('게시판 트리를 불러오지 못했습니다. 네트워크를 확인해 주세요.');
          setBoardTrees({ TASK_INFO: [], COOPERATION: [] });
        }
      }

      try {
        if (!skipMilestoneFetch) {
          const projects = await fetchAllianceMilestoneProjectsAll(spaceId);
          if (!cancelled) setMilestoneProjects(Array.isArray(projects) ? projects : []);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setMilestoneTreeError('마일스톤 프로젝트 목록을 불러오지 못했습니다. 네트워크를 확인해 주세요.');
          setMilestoneProjects([]);
        }
      } finally {
        if (!cancelled) setTreesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceId, skipBoardFetch, skipMilestoneFetch]);

  useEffect(() => {
    if (treesLoading) return;
    if (spaceId && roleRow?.id && serverPermissions === undefined) return;
    const server = spaceId && roleRow?.id ? serverPermissions : undefined;
    const initialFromProps = config?.initialData?.initialPermissions ?? roleRow?.permissions;
    const p = buildInitialPermissions(roleRow, initialFromProps, dynamicMenus, server);
    const roleCd = roleRow?.id ?? roleRow?.roleKey;
    setPerms({
      ...p,
      boards: mergeMilestoneSlots(mergeBoardSlots(p.boards || {}, boardTrees, roleCd), milestoneProjects, roleCd),
    });
  }, [
    treesLoading,
    boardTrees,
    milestoneProjects,
    serverPermissions,
    spaceId,
    roleKey,
    dynamicMenuSig,
    roleRow,
    dynamicMenus,
    config?.initialData?.initialPermissions,
  ]);

  const toggleFixed = useCallback((section, key) => {
    setPerms((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: !prev[section][key] },
    }));
  }, []);

  const toggleBoard = useCallback(
    (boardKey, field) => {
      setPerms((prev) => {
        const cur = prev.boards?.[boardKey] || { ...boardSlotFallback };
        return {
          ...prev,
          boards: {
            ...(prev.boards || {}),
            [boardKey]: { ...cur, [field]: !cur[field] },
          },
        };
      });
    },
    [boardSlotFallback],
  );

  const handleCancel = () => onClose(false);

  const handleSave = async () => {
    const name = String(roleName || roleRow?.roleLabelKo || '').trim();
    if (!name) {
      window.alert(t('popup.consortiumRoleManage.alertEnterRoleName'));
      return;
    }
    const desc = String(description || roleRow?.description || '').trim();
    const payload = {
      ...perms,
      meta: {
        roleName: name,
        description: desc,
      },
    };
    if (spaceId && roleRow?.id) {
      try {
        await saveAllianceSpaceRolePermission(spaceId, roleRow.id, payload);
      } catch (e) {
        console.error(e);
        window.alert(t('popup.consortiumRoleManage.alertSaveFailed'));
        return;
      }
    }
    onClose(true, {
      roleName: name,
      description: desc,
      permissions: payload,
      roleRow,
    });
  };

  const dynamicList = useMemo(
    () =>
      (dynamicMenus || [])
        .map((m) => ({
          id: String(m?.id ?? m?.menuId ?? ''),
          name: String(m?.name ?? m?.menuName ?? m?.title ?? '').trim() || t('popup.consortiumRoleManage.unnamedMenu'),
        }))
        .filter((m) => m.id),
    [dynamicMenus, t],
  );

  if (!roleRow) return null;

  const treesByGroupCd = (groupCd) => boardTrees[groupCd] ?? [];

  return (
    <PopupWrapper
      config={{
        ...config,
        title: t('popup.consortiumRoleManage.title'),
        width: config?.width ?? 680,
        maxWidth: config?.maxWidth ?? 'min(680px, 96vw)',
        maxHeight: config?.maxHeight ?? '90vh',
        leftButton: { text: t('popup.common.cancel'), color: 'btn-outline-secondary', submit: false },
        rightButton: { text: t('popup.common.saveChanges'), color: 'btn-dark', submit: true },
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-consortium-role-manage"
    >
      <div className="main-popup-body">
        <p className="small text-muted mb-3">{t('popup.consortiumRoleManage.subtitle')}</p>

        <div className="mb-3">
          <label className={FIELD_LABEL} htmlFor="consortium-role-name">
            {t('popup.consortiumRoleManage.roleNameLabel')}
          </label>
          <input
            id="consortium-role-name"
            type="text"
            className="form-control"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder={t('popup.consortiumRoleManage.roleNamePlaceholder')}
            autoComplete="off"
            maxLength={50}
            title={t('popup.consortiumRoleManage.roleNameTitle')}
          />
        </div>

        <div className="mb-3">
          <label className={FIELD_LABEL} htmlFor="consortium-role-desc">
            {t('popup.common.description')}
          </label>
          <textarea
            id="consortium-role-desc"
            className="form-control"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('popup.consortiumRoleManage.descriptionPlaceholder')}
            maxLength={300}
            title={t('popup.consortiumRoleManage.descriptionTitle')}
          />
        </div>

        <h3 className="h6 fw-semibold text-dark mb-2">{t('popup.consortiumRoleManage.permissionTitle')}</h3>
        <div className="border rounded-3 bg-light bg-opacity-50 p-3 mb-2">
          <div className="mb-4 pb-3 border-bottom border-light-subtle">
            <h4 className="h6 fw-semibold text-dark mb-1">{t('popup.consortiumRoleManage.consortiumSectionTitle')}</h4>
            <p className="small text-muted mb-2">
              {t('popup.consortiumRoleManage.consortiumSectionLead')}
            </p>
            {CONSORTIUM_MEMBER_ITEMS.map(({ key, title, description: desc }) => (
              <PermissionCheck
                key={key}
                id={`consortium-${key}`}
                checked={perms.consortium[key]}
                onChange={() => toggleFixed('consortium', key)}
                title={title}
                description={desc}
              />
            ))}
          </div>

          {treesLoading ? (
            <p className="small text-muted mb-3 mb-0">{t('popup.consortiumRoleManage.loadingTrees')}</p>
          ) : null}
          {boardTreesError ? <p className="small text-danger mb-3">{boardTreesError}</p> : null}
          {milestoneTreeError ? <p className="small text-danger mb-3">{milestoneTreeError}</p> : null}

          <MilestoneFolderPermissionBlock
            milestoneProjects={milestoneProjects}
            boards={perms.boards || {}}
            onToggleBoard={toggleBoard}
            isLast={false}
            emptyBoardFallback={boardSlotFallback}
          />

          {BOARD_TREE_GROUPS.map(({ groupCd, title, lead }) => (
            <AllianceBoardTreePermissionBlock
              key={groupCd}
              title={title}
              lead={lead}
              groupCd={groupCd}
              nodes={treesByGroupCd(groupCd)}
              boards={perms.boards || {}}
              onToggleBoard={toggleBoard}
              isLast={false}
              emptyBoardFallback={boardSlotFallback}
            />
          ))}

          <CrudAndManageGroup
            sectionId="calendar"
            title={t('popup.consortiumRoleManage.calendarTitle')}
            lead={t('popup.consortiumRoleManage.calendarLead')}
            perm={perms.calendar}
            onToggle={toggleFixed}
            withManage
            subjectLabel={t('popup.consortiumRoleManage.calendarSubject')}
            isLast={dynamicList.length === 0}
          />

          {dynamicList.length > 0 ? (
            <div className="mb-0">
              <h4 className="h6 fw-semibold text-dark mb-1">{t('popup.consortiumRoleManage.dynamicMenuTitle')}</h4>
              <p className="small text-muted mb-3">
                {t('popup.consortiumRoleManage.dynamicMenuLead')} <code className="small">/milestones</code> {t('popup.consortiumRoleManage.dynamicMenuLeadSuffix')}
              </p>
              {dynamicList.map((menu) => {
                const p = perms.dynamic[menu.id] || { ...boardSlotFallback };
                return (
                  <div key={menu.id} className="mb-4 pb-3 border-bottom border-white">
                    <h5 className="small fw-bold text-dark mb-2">{menu.name}</h5>
                    {CRUD_KEYS.map(({ key, label, descSuffix }) => (
                      <PermissionCheck
                        key={key}
                        id={`dyn-${menu.id}-${key}`}
                        checked={p[key]}
                        onChange={(v) => {
                          setPerms((prev) => {
                            const cur = prev.dynamic[menu.id] || { ...boardSlotFallback };
                            return {
                              ...prev,
                              dynamic: {
                                ...prev.dynamic,
                                [menu.id]: { ...cur, [key]: v },
                              },
                            };
                          });
                        }}
                        title={`${label} — ${menu.name}`}
                        description={`「${menu.name}」을(를) ${descSuffix}`}
                      />
                    ))}
                    <PermissionCheck
                      id={`dyn-${menu.id}-manage`}
                      checked={p.manage}
                      onChange={(v) => {
                        setPerms((prev) => {
                          const cur = prev.dynamic[menu.id] || { ...boardSlotFallback };
                          return {
                            ...prev,
                            dynamic: {
                              ...prev.dynamic,
                              [menu.id]: { ...cur, manage: v },
                            },
                          };
                        });
                      }}
                      title={MANAGE_KEY.label}
                      description={MANAGE_KEY.desc}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        <button type="button" className={`btn main-popup-left-btn ${getButtonClass('btn-outline-secondary')}`} onClick={handleCancel}>
          {t('popup.common.cancel')}
        </button>
        <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-dark')}`} onClick={handleSave}>
          {t('popup.common.saveChanges')}
        </button>
      </div>
    </PopupWrapper>
  );
};

export default ConsortiumRoleManagePopup;
